import {useEffect, useLayoutEffect, useRef} from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isConnectedFocusTarget(node) {
  return Boolean(node && typeof node.focus === 'function' && document.contains(node));
}

/**
 * Move focus to the preferred desktop panel control, with short rAF retries while
 * portaled/lazy content mounts.
 *
 * @param {import('react').RefObject<HTMLElement>} [initialFocusRef]
 * @param {import('react').RefObject<HTMLElement>} [panelRef]
 */
export function focusPdpPanelInitialTarget(initialFocusRef, panelRef) {
  let attempts = 0;

  const tryFocus = () => {
    const preferredTarget = initialFocusRef?.current;

    if (isConnectedFocusTarget(preferredTarget)) {
      preferredTarget.focus({preventScroll: true});

      return;
    }

    const fallbackTarget = panelRef?.current?.querySelector(FOCUSABLE_SELECTOR);

    if (isConnectedFocusTarget(fallbackTarget)) {
      fallbackTarget.focus({preventScroll: true});

      return;
    }

    if (attempts++ < 30) {
      requestAnimationFrame(tryFocus);
    }
  };

  tryFocus();
}

/**
 * Desktop PDP config / cover panels (portal, not Chakra Drawer): Esc dismiss,
 * initial focus into panel, ref for dialog landmark.
 *
 * @param {Object} options
 * @param {boolean} options.isActive - Panel is open
 * @param {Function} options.onDismiss - Back or close handler (same as visible dismiss control)
 * @param {import('react').RefObject<HTMLElement>} [options.initialFocusRef] - Preferred first focus target
 * @param {boolean} [options.manageInitialFocus=true] - Focus initial target when panel opens
 * @param {*} [options.focusKey] - Re-run initial focus when this value changes
 */
export function usePdpConfigPanelA11y({
  focusKey,
  initialFocusRef,
  isActive,
  manageInitialFocus = true,
  onDismiss,
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isActive || typeof onDismiss !== 'function') return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      event.stopPropagation();
      onDismiss();
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isActive, onDismiss]);

  useLayoutEffect(() => {
    if (!isActive || !manageInitialFocus) return undefined;

    focusPdpPanelInitialTarget(initialFocusRef, panelRef);

    return undefined;
  }, [focusKey, initialFocusRef, isActive, manageInitialFocus]);

  return {panelRef};
}
